import React, { useEffect, useState, useRef } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import pegman from '../assets/pegman.png';

const useGoogleMapsScript = (apiKey) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.google) {
      setIsScriptLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => setIsScriptLoaded(true);
    document.head.appendChild(script);
  }, [apiKey]);

  return isScriptLoaded;
};

const SoilProbeLocation = () => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const isScriptLoaded = useGoogleMapsScript(apiKey);
  const [markers, setMarkers] = useState([]);
  const [isAddingPin, setIsAddingPin] = useState(false);
  const [isDeletingProbe, setIsDeletingProbe] = useState(false);
  const [isMapLocked, setIsMapLocked] = useState(false);
  const [probeNumber, setProbeNumber] = useState(''); // New state for probe number
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentMarker, setCurrentMarker] = useState(null);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 51.059933193723126, lng: -114.14539808499006 });
  const [farmerLocation, setFarmerLocation] = useState({ lat: 51.059933193723126, lng: -114.14539808499006 });
  const [savedProbes, setSavedProbes] = useState([]);
  const [selectedProbe, setSelectedProbe] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedProbeMarker, setSelectedProbeMarker] = useState(null);
  const [fieldName, setFieldName] = useState('');  
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [probeToDelete, setProbeToDelete] = useState(null);

  // ORIGINAL
  // useEffect(() => {
  //   if (navigator.geolocation) {
  //     const watchId = navigator.geolocation.watchPosition(
  //       (position) => {
  //         const { latitude, longitude } = position.coords;
  //         // Update both farmer's location and map center
  //         setFarmerLocation({ lat: latitude, lng: longitude });
  //         setMapCenter({ lat: latitude, lng: longitude });
  //       },
  //       (error) => {
  //         console.error("Error getting location:", error);
  //       },
  //       { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 }
  //     );
  //     return () => navigator.geolocation.clearWatch(watchId);
  //   }
  // }, []);

  // FIX 1
  // const haversineDistance = (lat1, lon1, lat2, lon2) => {
  //   const R = 6371e3; // Earth's radius in meters
  //   const φ1 = (lat1 * Math.PI) / 180;
  //   const φ2 = (lat2 * Math.PI) / 180;
  //   const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  //   const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  
  //   const a =
  //     Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
  //     Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  //   const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  //   const distance = R * c; // Distance in meters
  //   return distance;
  // };
  
  // useEffect(() => {
  //   if (navigator.geolocation) {
  //     const watchId = navigator.geolocation.watchPosition(
  //       (position) => {
  //         const { latitude, longitude } = position.coords;
  
  //         // Check if the location has changed significantly (e.g., more than 10 meters)
  //         if (
  //           haversineDistance(farmerLocation.lat, farmerLocation.lng, latitude, longitude) > 5
  //         ) {
  //           setFarmerLocation({ lat: latitude, lng: longitude });
  //           setMapCenter({ lat: latitude, lng: longitude });
  //         }
  //       },
  //       (error) => {
  //         console.error("Error getting location:", error);
  //       },
  //       { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 }
  //     );
  
  //     return () => navigator.geolocation.clearWatch(watchId);
  //   }
  // }, [farmerLocation]); // Dependency array ensures updates only occur when the location changes significantly  

  // FIX 2
  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c; // Distance in meters
    return distance;
  };

  // Smooth transition function
  const smoothMove = (start, end, duration) => {
    const steps = 50; // Number of steps for smooth transition
    const intervalTime = duration / steps;
    const deltaLat = (end.lat - start.lat) / steps;
    const deltaLng = (end.lng - start.lng) / steps;

    let progress = 0;

    // Check if already moving to prevent overlapping animations
    if (isMoving) return;  // If an animation is already running, don't start another

    setIsMoving(true); // Set the animation state to true

    const animation = setInterval(() => {
      progress++;
      setFarmerLocation(prev => ({
        lat: prev.lat + deltaLat,
        lng: prev.lng + deltaLng
      }));

      if (progress >= steps) {
        clearInterval(animation);
        setFarmerLocation(end); // Ensure it reaches the final destination
        setIsMoving(false);  // Reset the animation state
      }
    }, intervalTime);
  };

  // State to track if the animation is in progress
  const [isMoving, setIsMoving] = useState(false);

  useEffect(() => {
    if (navigator.geolocation) {
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const newLocation = { lat: latitude, lng: longitude };

          // Check if the location has changed significantly (e.g., more than 10 meters)
          if (haversineDistance(farmerLocation.lat, farmerLocation.lng, latitude, longitude) > 10) {
            // Smoothly move to the new position only if not already animating
            if (!isMoving) {
              smoothMove(farmerLocation, newLocation, 1000); // 1000ms = 1-second transition
              setMapCenter(newLocation); // Optionally update map center
            }
          }
        },
        (error) => {
          console.error("Error getting location:", error);
        },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 }
      );

      return () => navigator.geolocation.clearWatch(watchId);
    }
  }, [farmerLocation, isMoving]); // Dependency on farmerLocation and isMoving

  const fetchSavedProbes = async () => {
    const token = localStorage.getItem("access");
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/saved-probes/`, {
        headers: {
          Authorization: `JWT ${token}`,
          "Content-Type": "application/json",
        },
      });
  
      if (!response.ok) {
        throw new Error("Failed to fetch saved probes");
      }
  
      const data = await response.json();
      setSavedProbes(data);
    } catch (error) {
      console.error("Error fetching saved probes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedProbes();
  }, []);

  const mapRef = useRef();

  const handleOnLoad = (map) => {
    map.setMapTypeId('satellite');
  };

  const handleProbeSelect = (event) => {
    setSelectedProbe(event.target.value);
  };

  const handleAddProbe = () => {
    if (farmerLocation) {
      const newMarker = { lat: farmerLocation.lat, lng: farmerLocation.lng, name: '', number: '' };
      setCurrentMarker(newMarker);
      setIsModalOpen(true);
    }
  };

  const handleSaveProbe = () => {
    if (currentMarker && probeNumber.trim() !== '') {
      setMarkers((prevMarkers) => [
        ...prevMarkers, 
        { 
          ...currentMarker, 
          number: probeNumber 
        }
      ]);
      setProbeNumber('');
      setIsModalOpen(false);
      setCurrentMarker(null);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setProbeNumber('');
    setCurrentMarker(null);
  };

  const toggleAddProbe = () => {
    setIsAddingPin((prev) => {
      if (!prev) {
        setIsDeletingProbe(false);
        handleAddProbe();
      }
      return !prev;
    });
  };

  const toggleDeleteProbe = () => {
    setIsDeletingProbe((prev) => {
      if (!prev) setIsAddingPin(false);
      return !prev;
    });
  };

  const toggleMapLock = () => {
    setIsMapLocked(!isMapLocked);
  };

  const handleDeleteProbe = (indexToDelete) => {
    setMarkers((prevMarkers) => prevMarkers.filter((_, index) => index !== indexToDelete));
  };

  const saveProbes = async () => {  
    if (!fieldName || markers.length === 0) {
      setModalMessage("Please provide a field name and ensure probes are placed.");
      setShowSuccessModal(true);
      console.log("Save failed: Missing field name or markers.");
      return;
    }
  
    const probeData = markers.map((marker) => [marker.lat, marker.lng, marker.number]);
    const token = localStorage.getItem("access");
  
    try {
      const requestBody = {
        fieldName: fieldName,
        probes: probeData,
      };
  
      let response;
      if (selectedProbe) {
        console.log(`Updating probe group: ${selectedProbe}`);
  
        response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/update-probe-group/${selectedProbe}/`,
          {
            method: "PUT",
            headers: {
              Authorization: `JWT ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          }
        );
      } else {
        console.log("Creating new probe group...");
  
        response = await fetch(`${process.env.REACT_APP_API_URL}/api/save-probes/`, {
          method: "POST",
          headers: {
            Authorization: `JWT ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });
      }
  
      console.log("Response received:", response);
  
      if (!response.ok) {
        throw new Error("Failed to save probes.");
      }
  
      setModalMessage("Probes saved successfully!");
      setShowSuccessModal(true);
      setIsSuccessModalOpen(true);
  
      fetchSavedProbes();
    } catch (err) {
      console.error("Error saving probes:", err);
      setModalMessage("Error saving probes.");
      setShowSuccessModal(true);
    }
  };  

  const clearMap = () => {
    setMarkers([]);         // Clear all placed markers
    setSelectedProbe("");   // Reset dropdown selection to default
    setFieldName("");       // Clear the field name input
  };  

  const saveModal = (
    <div
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
        zIndex: 1000,
      }}
    >
      <h3>Your probes are on this field</h3>
      <input
        type="text"
        value={fieldName}
        onChange={(e) => setFieldName(e.target.value)}
        placeholder="Enter Field Name"
        disabled={!!selectedProbe} // Disable editing if an existing probe is selected
        style={{
          width: "100%",
          padding: "10px",
          fontSize: "16px",
          border: "1px solid #ccc",
          borderRadius: "5px",
          backgroundColor: selectedProbe ? "#f5f5f5" : "white",
          color: selectedProbe ? "#555" : "black",
        }}
      />
      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
        <button
          onClick={saveProbes}
          style={{
            padding: '10px',
            backgroundColor: '#4CAF50',
            color: 'white',
            borderRadius: '5px',
          }}
        >
          Save
        </button>
        <button
          onClick={() => setIsSaveModalOpen(false)}
          style={{
            padding: '10px',
            backgroundColor: '#f44336',
            color: 'white',
            borderRadius: '5px',
          }}
        >
          Close
        </button>
      </div>
    </div>
  );

  if (!isScriptLoaded) {
    return <div>Loading...</div>;
  }

  const handleSavedProbeSelect = (event) => {
    const selectedProbeId = event.target.value;
  
    const selectedProbeGroup = savedProbes.find(
      (probeGroup) => probeGroup.id.toString() === selectedProbeId
    );
  
    if (selectedProbeGroup) {
      setSelectedProbe(selectedProbeGroup.id);
  
      // Auto-fill field name when selecting a design
      setFieldName(selectedProbeGroup.field_name);
  
      setMapCenter({
        lat: parseFloat(selectedProbeGroup.probes[0][0]),
        lng: parseFloat(selectedProbeGroup.probes[0][1]),
      });
  
      // Update markers with correct data format
      const markers = selectedProbeGroup.probes.map((probe) => ({
        lat: probe[0],
        lng: probe[1],
        name: selectedProbeGroup.field_name, // Auto-fill the name in marker
        number: probe[2] || '', // Handle both old and new data formats
      }));
  
      setMarkers(markers);
    }
  };  

  const deleteSavedProbeGroup = async () => {
    if (!probeToDelete) return;
  
    const token = localStorage.getItem("access");
  
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/delete-probe-group/${probeToDelete}/`, {
        method: "DELETE",
        headers: {
          Authorization: `JWT ${token}`,
          "Content-Type": "application/json",
        },
      });
  
      if (!response.ok) {
        throw new Error("Failed to delete probe group.");
      }
  
      alert("Probe group deleted successfully!");
      
      // Update dropdown by filtering out the deleted probe group
      setSavedProbes((prev) => prev.filter((probe) => probe.id !== probeToDelete));
      
      // Close modal
      setIsDeleteModalOpen(false);
      setProbeToDelete(null);
      setMarkers([]);
    } catch (err) {
      console.error(err);
      alert("Error deleting probe group.");
    }
  };
  
  const openDeleteModal = (probeId) => {
    setProbeToDelete(probeId);
    setIsDeleteModalOpen(true);
  };
  
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '10px',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <GoogleMap
          id="my-map"
          mapContainerStyle={{
            height: '500px',
            width: '500px',
            borderRadius: '10px',
            border: '1px solid #ccc',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          }}
          center={mapCenter}
          zoom={16}
          onLoad={handleOnLoad}
          options={{
            draggable: !isMapLocked,
            scrollwheel: !isMapLocked,
            disableDoubleClickZoom: isMapLocked,
            gestureHandling: isMapLocked ? 'none' : 'auto',
            tilt: 0,
          }}
        >
          {/* Farmer's Location */}
          {farmerLocation && (
            <Marker
              position={farmerLocation}
              icon={{
                url: pegman,
                scaledSize: new window.google.maps.Size(20, 40),
              }}
            />
          )}

          {/* Existing Probe Markers */}
          {markers.map((marker, index) => (
            <Marker
              key={`marker-${index}`}
              position={{ lat: marker.lat, lng: marker.lng }}
              label={{
                text: `${marker.name} #${marker.number} (${marker.lat.toFixed(4)}, ${marker.lng.toFixed(4)})`,
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
              onClick={() => {
                if (isDeletingProbe) {
                  handleDeleteProbe(index);
                }
              }}
              icon={{
                path: 'M 0,0 m -5,0 a 5,5 0 1,0 10,0 a 5,5 0 1,0 -10,0',
                fillColor: 'red',
                fillOpacity: 1,
                scale: 2,
                strokeColor: 'black',
                strokeWeight: 2,
              }}
            />
          ))}

          {/* Selected Probe Marker */}
          {selectedProbeMarker && (
            <Marker
              position={{ lat: selectedProbeMarker.lat, lng: selectedProbeMarker.lng }}
              label={{
                text: `${selectedProbeMarker.name} #${selectedProbeMarker.number} (${selectedProbeMarker.lat.toFixed(4)}, ${selectedProbeMarker.lng.toFixed(4)})`,
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
              icon={{
                path: 'M 0,0 m -5,0 a 5,5 0 1,0 10,0 a 5,5 0 1,0 -10,0',
                fillColor: 'green',
                fillOpacity: 1,
                scale: 2,
                strokeColor: 'black',
                strokeWeight: 2,
              }}
            />
          )}
        </GoogleMap>
      </div>
    {loading ? (
      <p>Loading...</p>
    ) : (
      <select
        value={selectedProbe || ""}  // Ensures reset to default
        onChange={handleSavedProbeSelect}
        style={{ width: '250px', padding: '10px', marginTop: '10px' }}
      >
        <option value="" disabled>
          Select Saved Probes
        </option>
        {savedProbes.map((probeGroup) =>
          probeGroup && probeGroup.probes && Array.isArray(probeGroup.probes) ? (
            <option key={probeGroup.id} value={probeGroup.id}>
              {probeGroup.field_name} - {probeGroup.probes.length > 0 ? `${probeGroup.probes.length} probes` : "No probes available"}
            </option>
          ) : null
        )}
      </select>
    )}

      {/* Buttons Section */}
        <div
          style={{
            width: '100%',
            maxWidth: '300px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '20px',
            alignItems: 'center',
            marginTop: '20px',
          }}
        >
          {/* Place Probe Button */}
          <button
            onClick={toggleAddProbe}
            style={{
              padding: '10px 20px',
              backgroundColor: isAddingPin ? '#00bfff' : '#1e90ff',
              color: 'white',
              borderRadius: '5px',
              width: '100%',
              height: '50px',
              fontSize: '16px',
            }}
          >
            {isAddingPin ? 'Cancel' : 'Place Probe'}
          </button>
          {/* Finish & Save Button */}
          <button
            onClick={() => setIsSaveModalOpen(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#4CAF50',
              color: 'white',
              borderRadius: '5px',
              width: '100%',
              fontSize: '16px',
              height: '50px',
            }}
          >
            Finish & Save
          </button>
          {/* Delete Probe Button */}
          <button
            onClick={toggleDeleteProbe}
            style={{
              padding: '10px 20px',
              backgroundColor: isDeletingProbe ? '#cd5c5c' : '#dc143c',
              color: 'white',
              borderRadius: '5px',
              width: '100%',
              height: '50px',
              fontSize: '16px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {isDeletingProbe ? 'Stop' : 'Remove Probe'}
          </button>
          {/* Lock/Unlock Map Button */}
          <button
            onClick={toggleMapLock}
            style={{
              padding: '10px 20px',
              backgroundColor: isMapLocked ? '#778899' : '#808080',
              color: 'white',
              borderRadius: '5px',
              width: '100%',
              height: '50px',
              fontSize: '16px',
            }}
          >
            {isMapLocked ? 'Unlock Map' : 'Lock Map'}
          </button>
          {/* / Clear Button */}
          <button
            onClick={clearMap}
            style={{
              padding: '10px 20px',
              width: '100%',
              maxWidth: '200px',
              backgroundColor: '#4682b4',
              color: 'white',
              borderRadius: '5px',
              fontSize: '16px',
            }}
          >
            New Design
          </button>
        {/* Delete Button */}
        {selectedProbe && (
          <button
            onClick={() => openDeleteModal(selectedProbe)}
            style={{
              padding: '10px 20px',
              width: '100%',
              maxWidth: '200px',
              backgroundColor: '#b22222',
              color: 'white',
              borderRadius: '5px',
              fontSize: '16px',
            }}
          >
            Delete Probe Collection
          </button>
        )}
        </div>
      {/* Save Modal */}
      {isSaveModalOpen && saveModal}
    {/* Probe Labeling Modal - Now with Probe Number field */}
    {isModalOpen && (
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          zIndex: 1000,
        }}
      >
        <h4>Label Your Probe</h4>
        <input
          type="text"
          value={probeNumber}
          onChange={(e) => setProbeNumber(e.target.value)}
          placeholder="Probe number (e.g. 32)"
          style={{ width: '100%', padding: '10px', marginTop: '10px' }}
        />
        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
          <button
            onClick={handleSaveProbe}
            style={{
              padding: '10px',
              backgroundColor: '#4CAF50',
              color: 'white',
              borderRadius: '5px',
            }}
          >
            Place
          </button>
          <button
            onClick={handleCloseModal}
            style={{
              padding: '10px',
              backgroundColor: '#f44336',
              color: 'white',
              borderRadius: '5px',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    )}
    {/* Success Modal */}
    {isSuccessModalOpen && (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "10px",
          boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
          textAlign: "center",
          zIndex: 1000,
        }}
      >
        <h3 style={{ color: "green" }}>{modalMessage}</h3>
        <button
          onClick={() => setIsSuccessModalOpen(false)}
          style={{
            padding: "10px",
            backgroundColor: "#4CAF50",
            color: "white",
            borderRadius: "5px",
            cursor: "pointer",
            fontSize: "16px",
            marginTop: "10px",
          }}
        >
          Close
        </button>
      </div>
    )}

    {isDeleteModalOpen && (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
          zIndex: 1000,
        }}
      >
        <h3>Confirm Delete</h3>
        <p>Are you sure you want to delete this collection of place soil probes?</p>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
          <button
            onClick={deleteSavedProbeGroup}
            style={{
              padding: "10px",
              backgroundColor: "#f44336",
              color: "white",
              borderRadius: "5px",
            }}
          >
            Delete
          </button>
          <button
            onClick={() => setIsDeleteModalOpen(false)}
            style={{
              padding: "10px",
              backgroundColor: "#ccc",
              color: "black",
              borderRadius: "5px",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    )}
  </div>
);
};

export default SoilProbeLocation;